import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    Avatar,
    Box,
    Typography,
    Chip,
    Tooltip,
    CircularProgress,
    Alert,
    Grid,
    Divider,
    Skeleton
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useBitcoinPrice } from '../../state/context/bitcoin.context';
import { API_BASE_URL } from '../../utils/constants';

// Main App component
export default function DepositComponent() {
    const { t } = useTranslation();
    const { price: btcPrice } = useBitcoinPrice();
    const navigate = useNavigate();
    const [deposits, setDeposits] = useState({ data: [], page: 1, totalPages: 1, totalResults: 0 });
    const [userNames, setUserNames] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // API endpoint URL for deposits
    // Calculate startDate as 6 hours ago in ISO format
    const sixHoursAgo = new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString();
    const DEPOSITS_API_URL = `${ API_BASE_URL }/explorer/deposits?limit=10&sortBy=amount&sortOrder=desc&startDate=${ encodeURIComponent(sixHoursAgo) }`;
    // Base API endpoint URL for user details
    const USER_API_BASE_URL = "https://api.odin.fun/v1/user/";

    useEffect(() => {
        const fetchDepositsAndUserDetails = async () => {
            setLoading(true);
            setError(null);
            try {
                // 1. Fetch deposit data
                const depositsResponse = await fetch(DEPOSITS_API_URL);
                if (!depositsResponse.ok) {
                    throw new Error(`HTTP error! status: ${ depositsResponse.status } from deposits API`);
                }
                const depositsData = await depositsResponse.json();
                setDeposits(depositsData);

                // 2. Extract unique user IDs from deposit data
                const userIds = depositsData.data.map(deposit => deposit.user);
                const uniqueUserIds = [...new Set(userIds)];

                // 3. Fetch username for each unique user ID
                const fetchedUsernames = {};
                const fetchPromises = uniqueUserIds.map(async (userId) => {
                    try {
                        const userResponse = await fetch(`${ USER_API_BASE_URL }${ userId }`);
                        if (userResponse.ok) {
                            const userData = await userResponse.json();
                            if (userData.username) {
                                fetchedUsernames[userId] = userData.username;
                            }
                        } else {
                            console.warn(`Could not fetch user data for ID ${ userId }: HTTP status ${ userResponse.status }`);
                        }
                    } catch (userErr) {
                        console.warn(`Error fetching user data for ID ${ userId }:`, userErr);
                    }
                });

                await Promise.all(fetchPromises);
                setUserNames(fetchedUsernames);

            } catch (err) {
                console.error("Error fetching data:", err);
                setError(t('explorer.failedToLoadData'));
            } finally {
                setLoading(false);
            }
        };

        fetchDepositsAndUserDetails();
    }, []);

    // Function to format the amount with the new divisor (1e11)
    const formatAmount = (amount) => {
        // Convert to a number and divide by 1e11 for the correct unit
        const readableAmount = (Number(amount) / 1e11).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 11 // Adjust maximumFractionDigits to accommodate more precision if needed
        });
        return `${ readableAmount } BTC`; // Changed from 'units' to 'BTC'
    };

    // Function to format amount with USD conversion
    const formatAmountWithUSD = (amount) => {
        const btcAmount = Number(amount) / 1e11;
        const btcFormatted = btcAmount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 8
        });

        if (btcPrice) {
            const usdValue = btcAmount * btcPrice;
            const usdFormatted = usdValue.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
            return {
                btc: `₿ ${ btcFormatted }`,
                usd: `$${ usdFormatted }`
            };
        }

        return {
            btc: `₿ ${ btcFormatted }`,
            usd: null
        };
    };

    // Function to format the timestamp
    const formatTime = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleString();
    };

    // Function to shorten addresses
    const shortenAddress = (address, length = 10) => {
        if (!address || address.length <= length) return address;
        return `${ address.slice(0, length / 2) }...${ address.slice(-length / 2) }`;
    };

    // Navigation handlers
    const handleTransactionClick = (txId) => {
        window.open(`/explorer/transaction/${ txId }`);
    };

    const handleUserClick = (userId) => {
        window.open(`/explorer/user/${ userId }`);
    };

    // Skeleton Loading Component for Deposits
    const DepositSkeleton = () => (
        <Grid container spacing={2}>
            {[...Array(5)].map((_, index) => (
                <Grid item xs={12} key={index}>
                    <Card sx={{ mb: 1.5, bgcolor: 'background.paper' }}>
                        <CardContent sx={{ py: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                {/* User Section Skeleton */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: '200px' }}>
                                    <Skeleton variant="circular" width={48} height={48} />
                                    <Box>
                                        <Skeleton variant="text" width={120} height={24} />
                                        <Skeleton variant="text" width={100} height={20} />
                                    </Box>
                                </Box>

                                {/* Amount Section Skeleton */}
                                <Box sx={{ textAlign: 'center', minWidth: '140px' }}>
                                    <Skeleton variant="text" width={60} height={20} sx={{ mx: 'auto', mb: 0.5 }} />
                                    <Skeleton variant="rounded" width={100} height={32} sx={{ mx: 'auto', mb: 0.5 }} />
                                    <Skeleton variant="text" width={80} height={16} sx={{ mx: 'auto' }} />
                                </Box>

                                {/* Transaction Details Skeleton */}
                                <Box sx={{ minWidth: '300px' }}>
                                    <Box sx={{ display: 'flex', gap: 4 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Skeleton variant="text" width={80} height={16} />
                                            <Skeleton variant="text" width={140} height={20} />
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Skeleton variant="text" width={100} height={16} />
                                            <Skeleton variant="text" width={140} height={20} />
                                        </Box>
                                    </Box>
                                    {/* Sender Addresses Skeleton */}
                                    <Box sx={{ mt: 1 }}>
                                        <Skeleton variant="text" width={120} height={16} sx={{ mb: 0.5 }} />
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Skeleton variant="rounded" width={60} height={20} />
                                            <Skeleton variant="rounded" width={60} height={20} />
                                            <Skeleton variant="rounded" width={60} height={20} />
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );

    return (
        <Box sx={{
            p: 3,
            minHeight: '100vh',
            fontFamily: 'Inter, sans-serif'
        }}>
            <Card sx={{ maxWidth: '1200px', mx: 'auto', mb: 3 }}>
                <CardContent sx={{ pb: 1 }}>
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{
                            fontWeight: 600,
                            mb: 0.5
                        }}
                    >
                        {t('explorer.topRecentDeposits')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('explorer.latestBitcoinDepositTransactions')}
                    </Typography>
                </CardContent>
            </Card>

            {loading && (
                <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
                    <DepositSkeleton />
                </Box>
            )}

            {error && (
                <Alert
                    severity="error"
                    sx={{
                        mb: 3,
                        maxWidth: '800px',
                        mx: 'auto',
                        borderRadius: 2
                    }}
                >
                    {error}
                </Alert>
            )}

            {!loading && !error && (
                <>
                    <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
                        {deposits.data.length > 0 ? (
                            <Grid container spacing={2}>
                                {deposits.data.map((row) => (
                                    <Grid item xs={12} key={row._id}>
                                        <Card sx={{ mb: 1.5, bgcolor: 'background.paper' }}>
                                            <CardContent sx={{ py: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                                    {/* User Section */}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                            minWidth: '200px',
                                                            cursor: 'pointer',
                                                            borderRadius: 1,
                                                            p: 0.5,
                                                            '&:hover': {
                                                                bgcolor: 'action.hover'
                                                            }
                                                        }}
                                                        onClick={() => handleUserClick(row.user)}
                                                    >
                                                        <Avatar
                                                            alt="User Avatar"
                                                            src={`https://images.odin.fun/user/${ row.user }`}

                                                            sx={{
                                                                width: 48,
                                                                height: 48,
                                                                borderRadius: '50%'
                                                            }}
                                                        />
                                                        <Box>
                                                            <Typography
                                                                variant="subtitle1"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    '&:hover': {
                                                                        color: 'primary.main'
                                                                    }
                                                                }}
                                                            >
                                                                {userNames[row.user] || row.user}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {formatTime(row.time)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    {/* Amount Section */}
                                                    <Box sx={{ textAlign: 'center', minWidth: '140px' }}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            {t('explorer.amount')}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                            <Chip
                                                                label={formatAmountWithUSD(row.amount).btc}
                                                                color="success"
                                                                variant="outlined"
                                                                sx={{ fontWeight: 600 }}
                                                            />
                                                            {formatAmountWithUSD(row.amount).usd && (
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: 'text.secondary',
                                                                        fontSize: '0.75rem'
                                                                    }}
                                                                >
                                                                    {formatAmountWithUSD(row.amount).usd}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>

                                                    {/* Transaction Details */}
                                                    <Box sx={{ minWidth: '300px' }}>
                                                        <Box sx={{ display: 'flex', gap: 4 }}>
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                    {t('transactionId')}
                                                                </Typography>
                                                                <Tooltip title={`${ row.txId } - Click to view transaction details`} placement="top">
                                                                    <Typography
                                                                        variant="body2"
                                                                        onClick={() => handleTransactionClick(row.txId)}
                                                                        sx={{
                                                                            fontFamily: 'monospace',
                                                                            cursor: 'pointer',
                                                                            fontSize: '0.8rem',
                                                                            color: 'primary.main',
                                                                            textDecoration: 'underline',
                                                                            textDecorationColor: 'transparent',
                                                                            transition: 'all 0.2s ease',
                                                                            '&:hover': {
                                                                                color: 'primary.dark',
                                                                                textDecorationColor: 'primary.dark'
                                                                            }
                                                                        }}
                                                                    >
                                                                        {shortenAddress(row.txId, 12)}
                                                                    </Typography>
                                                                </Tooltip>
                                                            </Box>
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                    {t('explorer.depositAddress')}
                                                                </Typography>
                                                                <Tooltip title={row.address} placement="top">
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontFamily: 'monospace',
                                                                            cursor: 'pointer',
                                                                            fontSize: '0.8rem',
                                                                            '&:hover': { color: 'primary.main' }
                                                                        }}
                                                                    >
                                                                        {shortenAddress(row.address)}
                                                                    </Typography>
                                                                </Tooltip>
                                                            </Box>
                                                        </Box>

                                                        {/* Sender Addresses */}
                                                        {row.senderAddresses && row.senderAddresses.length > 0 && (
                                                            <Box sx={{ mt: 1 }}>
                                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                                                                    {t('explorer.senderAddresses')}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                    {row.senderAddresses.slice(0, 3).map((sender, index) => (
                                                                        <Tooltip key={index} title={sender} placement="top">
                                                                            <Chip
                                                                                label={shortenAddress(sender, 8)}
                                                                                size="small"
                                                                                variant="outlined"
                                                                                sx={{ fontSize: '0.7rem', height: '20px' }}
                                                                            />
                                                                        </Tooltip>
                                                                    ))}
                                                                    {row.senderAddresses.length > 3 && (
                                                                        <Chip
                                                                            label={`+${ row.senderAddresses.length - 3 } more`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            sx={{ fontSize: '0.7rem', height: '20px' }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Card>
                                <CardContent>
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="h6" color="text.secondary">
                                            {t('explorer.noDepositDataAvailable')}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
}
